import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import { useMutation } from '@apollo/client'
import styles from './RecallPlusView.module.scss'
import UserContext from '../context/UserContext'
import { RECORD_PRACTICE_MUTATION } from '../query/gqlQueries'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate, popScale } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SkipNextIcon from '@mui/icons-material/SkipNext'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

const COMPLETE_RATIO = 0.7 // hit this fraction of the sentence → done, move on
const STUCK_BEFORE_ACCEPT = 4 // no-progress tries before a word auto-fills (keep flowing)

const HINTS = [
  { key: 'none', label: 'None' },
  { key: 'few', label: 'A couple' },
  { key: 'half', label: 'Half' }
]

const stripGloss = (s) =>
  String(s || '')
    .replace(/\(([^)]*[A-Za-z][^)]*)\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

const practiceSideOf = (card) => {
  const t = card.translations?.[0]
  // `id` = the practised side's DB node id, so a completed line can feed SRS.
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { text: stripGloss(card.source), lang: card.sourceLanguage, prompt: t?.target || '', id: card.sourceId }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { text: stripGloss(t.target), lang: t.targetLanguage, prompt: card.source || '', id: t.targetId }
  }
  return { text: stripGloss(card.source), lang: card.sourceLanguage || 'eng', prompt: t?.target || '', id: card.sourceId }
}

const range = (n) => Array.from({ length: n }, (_, i) => i)
const shuffle = (arr) => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default function RecallPlusView({ cards, voiceSpeed, onClose }) {
  const { username } = useContext(UserContext)
  const [recordPracticeMutation] = useMutation(RECORD_PRACTICE_MUTATION)
  const recordItem = useCallback(
    (itemId, text, isCorrect) => {
      if (!itemId || !username) return
      const itemType = /\s/.test(String(text || '')) ? 'Sentence' : 'Word'
      recordPracticeMutation({ variables: { username, itemId, itemType, isCorrect } }).catch(() => {})
    },
    [recordPracticeMutation, username]
  )

  const lines = useMemo(() => (cards || []).map(practiceSideOf).filter((i) => i.text && i.text.trim()), [cards])

  const [hintLevel, setHintLevel] = useState('none')
  const [seq, setSeq] = useState(0) // total sentences advanced through
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [milestone, setMilestone] = useState(0)
  const [hits, setHits] = useState({ forSeq: -1, states: [] })
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micError, setMicError] = useState(null)
  const [peek, setPeek] = useState(false)
  const [flash, setFlash] = useState(false)

  const deckRef = useRef(shuffle(range(lines.length)))
  const lineIdx = lines.length ? deckRef.current[seq % deckRef.current.length] : 0
  const line = lines[lineIdx]
  const tokens = useMemo(() => tokenizeLine(line?.text, line?.lang), [line])
  const roman = useMemo(() => romanizationOf(line?.text, line?.lang), [line])
  const states = hits.forSeq === seq ? hits.states : []

  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const missRef = useRef(0)
  const lastFinalRef = useRef('')
  const liveHitsRef = useRef([])
  const lineRef = useRef(null)
  const givenRef = useRef(new Set()) // scaffold words (free, shown)
  const needRef = useRef(1)
  const tokensRef = useRef([])
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  useEffect(() => {
    lineRef.current = line
    tokensRef.current = tokens
  }, [line, tokens])

  // Rebuild the deck if the set size changes.
  useEffect(() => {
    deckRef.current = shuffle(range(lines.length))
  }, [lines.length])

  // Pop the combo counter as it climbs.
  useEffect(() => {
    if (combo > 0) popScale(comboRef.current)
  }, [combo])

  // Per-sentence setup: choose scaffold words, seed hits, set the win threshold.
  useEffect(() => {
    if (!line) return
    const n = tokens.length
    const shownCount = hintLevel === 'none' ? 0 : hintLevel === 'half' ? Math.floor(n * 0.5) : Math.min(n, Math.max(2, Math.round(n * 0.25))) // "a couple" ≈ 2 words
    const given = new Set(shuffle(range(n)).slice(0, shownCount))
    givenRef.current = given
    const fresh = tokens.map((_, i) => given.has(i))
    liveHitsRef.current = fresh
    setHits({ forSeq: seq, states: fresh })
    // Win at ~70% of the sentence, but always require voicing at least one blank.
    needRef.current = Math.max(1, Math.min(n, Math.max(Math.ceil(n * COMPLETE_RATIO), shownCount + 1)))
    completedRef.current = false
    missRef.current = 0
    lastFinalRef.current = ''
    setInterim('')
    setPeek(false)
  }, [seq, line, tokens, hintLevel])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setSeq((s) => {
      const ns = s + 1
      if (deckRef.current.length && ns % deckRef.current.length === 0) deckRef.current = shuffle(range(lines.length))
      return ns
    })
  }, [lines.length])

  const handleComplete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    // A recalled line → record a correct rep for spaced repetition.
    if (lineRef.current?.id) recordItem(lineRef.current.id, lineRef.current.text, true)
    setFlash(true)
    setTimeout(() => setFlash(false), 320)
    const el = cardRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 180 })
    }
    setCombo((c) => {
      const nc = c + 1
      setBest((b) => Math.max(b, nc))
      if (nc % 5 === 0) {
        celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 70, spread: 280 })
        setMilestone(nc)
        setTimeout(() => setMilestone(0), 1200)
      }
      return nc
    })
    advanceTimerRef.current = setTimeout(advance, 650)
  }, [advance, recordItem])

  // Threshold reached → fire.
  useEffect(() => {
    if (hits.forSeq !== seq || !line) return
    if (tokens.length === 0 || hits.states.length !== tokens.length) return
    const hitCount = hits.states.filter(Boolean).length
    if (hitCount >= needRef.current && !completedRef.current) handleComplete()
  }, [hits, seq, line, tokens.length, handleComplete])

  const applyTranscript = useCallback(
    (transcript, isFinal) => {
      if (!lineRef.current) return
      const tks = tokensRef.current
      const spoken = tokenizeTranscript(transcript, lineRef.current.lang)
      const base = liveHitsRef.current.length === tks.length ? liveHitsRef.current : tks.map(() => false)
      const next = [...base]
      let added = 0
      for (let ti = 0; ti < tks.length; ti++) {
        if (next[ti]) continue
        if (spoken.some((tok) => tokenMatches(tok, tks[ti]?.norm, lineRef.current?.lang))) {
          next[ti] = true
          added++
        }
      }
      liveHitsRef.current = next
      setHits({ forSeq: seq, states: next })

      if (isFinal && !completedRef.current) {
        const heard = transcript.trim()
        if (heard && heard !== lastFinalRef.current) {
          lastFinalRef.current = heard
          if (added > 0) missRef.current = 0
          else {
            missRef.current += 1
            if (missRef.current >= STUCK_BEFORE_ACCEPT) {
              missRef.current = 0
              const t = next.findIndex((h) => !h) // auto-fill a blank to keep it moving
              if (t >= 0) {
                next[t] = true
                liveHitsRef.current = next
                setHits({ forSeq: seq, states: next })
              }
            }
          }
        }
      }
    },
    [seq]
  )

  // ── recognizer ────────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    listeningRef.current = false
    setListening(false)
    sessionRef.current++
    try {
      recRef.current?.stop()
    } catch {
      /* stopped */
    }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setMicError('Speech recognition is not supported in this browser.')
      return
    }
    if (!lineRef.current) return
    setMicError(null)
    cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[lineRef.current.lang] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    let heardSound = false
    const stopWithError = (msg) => {
      listeningRef.current = false
      setListening(false)
      setMicError(msg)
    }
    rec.onsoundstart = () => {
      if (session === sessionRef.current) {
        heardSound = true
        deadSessionsRef.current = 0
      }
    }
    rec.onspeechstart = () => {
      if (session === sessionRef.current) {
        heardSound = true
        deadSessionsRef.current = 0
      }
    }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      let transcript = '',
        sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) sawFinal = true
      }
      setInterim(transcript.trim().slice(-60))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current || completedRef.current) return
      if (!heardSound) {
        deadSessionsRef.current++
        if (deadSessionsRef.current >= 4) {
          stopWithError('Mic is open but no sound is reaching it — check the input in the address-bar site settings.')
          return
        }
      }
      setTimeout(() => {
        if (session === sessionRef.current && listeningRef.current) {
          try {
            rec.start()
          } catch {
            /* raced */
          }
        }
      }, 450)
    }
    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try {
      rec.start()
    } catch {
      setTimeout(() => {
        if (session === sessionRef.current && listeningRef.current) {
          try {
            rec.start()
          } catch {
            /* give up */
          }
        }
      }, 400)
    }
  }, [applyTranscript])

  // Recycle the recognizer for each new sentence (fresh transcript + language).
  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try {
      recRef.current?.abort()
    } catch {
      /* fine */
    }
    const t = setTimeout(() => {
      if (listeningRef.current) startListening()
    }, 250)
    return () => clearTimeout(t)
  }, [seq]) // eslint-disable-line

  useEffect(
    () => () => {
      clearTimeout(advanceTimerRef.current)
      listeningRef.current = false
      sessionRef.current++
      try {
        recRef.current?.abort()
      } catch {
        /* fine */
      }
    },
    []
  )

  const skip = () => {
    setCombo(0)
    advance()
  }
  const hearLine = () => {
    if (line) speak(line.text, voices[line.lang] || voices['eng'], voiceSpeed)
  }

  if (lines.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}>
          <CloseIcon />
        </button>
        <div className={styles.empty}>Nothing to practise.</div>
      </div>
    )
  }

  const need = needRef.current
  const hitCount = states.filter(Boolean).length
  const pct = need ? Math.min(100, Math.round((hitCount / need) * 100)) : 0
  const recLangLabel = recognitionLangs[line?.lang] || 'en-US'

  return (
    <div className={`${styles.view} ${flash ? styles.flash : ''}`}>
      <button className={styles.closeBtn} onClick={onClose}>
        <CloseIcon />
      </button>

      <div className={styles.topBar}>
        <span className={styles.modeLabel}>Recall+ · {languages[line?.lang] || line?.lang}</span>
        <div className={styles.hintRow}>
          {HINTS.map((h) => (
            <button key={h.key} className={`${styles.hintChip} ${hintLevel === h.key ? styles.hintChipActive : ''}`} onClick={() => setHintLevel(h.key)}>
              {h.label}
            </button>
          ))}
        </div>
        <div className={styles.comboWrap}>
          <span className={styles.comboFire}>{combo >= 3 ? '🔥' : '⚡'}</span>
          <span className={styles.combo} ref={comboRef}>
            {combo}
          </span>
        </div>
      </div>

      <div className={styles.stage} key={seq}>
        <div className={styles.promptLabel}>say it in {languages[line?.lang] || line?.lang}</div>
        <div className={styles.prompt}>{line?.prompt || '—'}</div>

        <div className={styles.card} ref={cardRef}>
          <div className={styles.lineWords}>
            {tokens.map((w, wi) => {
              const isHit = states[wi]
              const given = givenRef.current.has(wi)
              const hidden = !isHit && !peek // not yet said & not peeking
              const cls = [styles.word, isHit ? (given ? styles.wordGiven : styles.wordHit) : '', hidden ? styles.wordHidden : ''].join(' ')
              return (
                <span key={wi} className={cls} onClick={hearLine}>
                  {w.raw}
                </span>
              )
            })}
          </div>
          {peek && roman && <div className={styles.roman}>{roman}</div>}
        </div>

        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <div className={styles.needLabel}>
          {hitCount} / {need} words — {Math.max(0, need - hitCount)} to fire
        </div>

        {milestone > 0 && <div className={styles.milestone}>🔥 {milestone} streak!</div>}
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.interim}>
          {micError ? micError : listening ? interim || `mic open — go! (${recLangLabel})` : 'tap the mic and say the sentence'}
        </div>
        <div className={styles.controls}>
          <button
            className={styles.sideBtn}
            onMouseDown={() => setPeek(true)}
            onMouseUp={() => setPeek(false)}
            onMouseLeave={() => setPeek(false)}
            onTouchStart={() => setPeek(true)}
            onTouchEnd={() => setPeek(false)}
            title="Hold to peek">
            <VisibilityIcon />
          </button>
          <button className={styles.sideBtn} onClick={hearLine} title="Hear it">
            <VolumeUpIcon />
          </button>
          <button
            className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`}
            onClick={() => (listening ? stopListening() : startListening())}
            title={listening ? 'Stop' : 'Speak'}>
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)">
            <SkipNextIcon />
          </button>
        </div>
        <div className={styles.progress}>
          #{seq + 1} · best {best}
        </div>
      </div>
    </div>
  )
}
